import React, { FC, useCallback, useMemo, useState } from 'react';
import { connect } from 'react-redux';
import { Layout, Row, Col, BackTop, message } from 'antd';
import { withRouter } from 'react-router';
import { getRecordsForCompositeTargetCollection } from '../../reducers/standard_reducer_utils';
import { getLowestBidderSupplierFacilityId } from '../../utils/DataAccessUtils';
import { PageLoadingPlaceholder } from '../page_loading_placeholder/PageLoadingPlaceholder';
import {
	biddersRestCrudThunksForBuyer,
	biddersRestCrudThunksForSupplier,
	changeBidderAwardStatusForBuyer,
	changeBidderAwardStatusForSupplier,
} from '../../thunks/bidders_thunks';
import { ROLE_TYPES } from '../../utils/DataConstants';
import BidderCard from './BidderCard';
import {
	requestsForProposalRestCrudThunksForBuyer,
	requestsForProposalRestCrudThunksForSupplier,
} from '../../thunks/requests_for_proposal_thunks';

const RFP_DETAIL_BIDDERS = 'RFP_DETAIL_BIDDERS';

require('./RFPDetailBidsPage.less');
const { Content } = Layout;

const RFPDetailBidsPage: FC<any> = ({
	getBidders,
	updateBidderAwardStatus,
	requestForProposal,
	bidders,
	requestsForProposalFetching,
	biddersFetching,
	userType,
	getRequestForProposal,
}): React.ReactElement => {
	const [awardUpdating, setAwardUpdating] = useState(false);

	const handleAwardStatusChange = useCallback(
		(requestForProposalId, supplierFacilityId, value) => {
			setAwardUpdating(true);
			updateBidderAwardStatus(requestForProposalId, supplierFacilityId, value)
				.then(() => {
					message.success(`Bidder status updated`);
					getBidders({ requestForProposalId }, RFP_DETAIL_BIDDERS);
					getRequestForProposal(requestForProposalId);
				})
				.catch(message.error)
				.finally(() => setAwardUpdating(false));
		},
		[getBidders, getRequestForProposal, updateBidderAwardStatus]
	);

	const associatedBidders = useMemo(
		() => getRecordsForCompositeTargetCollection(bidders, RFP_DETAIL_BIDDERS),
		[bidders]
	);
	const loading = useMemo(
		() => requestsForProposalFetching || biddersFetching,
		[biddersFetching, requestsForProposalFetching]
	);
	const actualBidders = useMemo(
		() => associatedBidders.filter((bidder) => bidder.proposalId),
		[associatedBidders]
	);

	const lowestBidderSupplierFacilityId = useMemo(
		() => getLowestBidderSupplierFacilityId(actualBidders),
		[actualBidders]
	);

	const bidderAwarded = useMemo(
		() => (actualBidders.filter((b) => b.awardStatus === 'awarded').length > 0 ? true : false),
		[actualBidders]
	);

	return loading ? (
		<PageLoadingPlaceholder />
	) : (
		<Content className="compareBidsPage" style={{ padding: '0 0.5em' }}>
			<BackTop />

			<Row style={{ margin: '0.5em -8px' }} gutter={16}>
				<Col span={24}>
					<div style={{ overflow: 'auto' }}>
						<div>
							<Row>
								<div className="flex flex-row gap-x-4">
									{actualBidders.map((bidder, idx) => (
										<BidderCard
											key={`bidder-${idx}`}
											bidder={bidder}
											bidderAwarded={bidderAwarded}
											awardUpdating={awardUpdating}
											requestForProposal={requestForProposal}
											handleAwardStatusChange={handleAwardStatusChange}
											lowestBidderSupplierFacilityId={lowestBidderSupplierFacilityId}
										/>
									))}
								</div>
							</Row>
						</div>
					</div>
				</Col>
			</Row>
		</Content>
	);
};

const mapStateToProps = (state) => ({
	requestForProposal: state.requests_for_proposal.detail,
	requestForProposalsFetching: state.requests_for_proposal.fetching,
	bidders: state.bidders,
	biddersFetching: state.bidders.fetching,
	proposals: state.proposals,
	proposalsFetching: state.proposals.fetching,
	currentUser: state.session.currentUser,
});

const mapDispatchToProps = (dispatch, ownProps) => ({
	updateBidderAwardStatus: (rfpId, supplierFacilityId, status) =>
		dispatch(
			ownProps.userType === ROLE_TYPES.SUPPLIER
				? changeBidderAwardStatusForSupplier(rfpId, supplierFacilityId, status)
				: changeBidderAwardStatusForBuyer(rfpId, supplierFacilityId, status)
		),
	getBidders: (params, targetCollectionName, pagination, sorting, filters) =>
		dispatch(
			ownProps.userType === ROLE_TYPES.SUPPLIER
				? biddersRestCrudThunksForSupplier.read(
						params,
						targetCollectionName,
						pagination,
						sorting,
						filters
				  )
				: biddersRestCrudThunksForBuyer.read(
						params,
						targetCollectionName,
						pagination,
						sorting,
						filters
				  )
		),
	getRequestForProposal: (id) =>
		dispatch(
			ownProps.userType === ROLE_TYPES.SUPPLIER
				? requestsForProposalRestCrudThunksForSupplier.readOne(id)
				: requestsForProposalRestCrudThunksForBuyer.readOne(id)
		),
});

const RFPCompareBidderPage = withRouter<any, any>(
	connect(mapStateToProps, mapDispatchToProps)(RFPDetailBidsPage)
);

export default connect(
	(state) => ({
		userType: (state as any).session.userType,
	}),
	() => ({})
)(RFPCompareBidderPage);
